/*
 * AOF — Mobile sticky add-to-cart observer
 *
 * Reveals the <aof-sticky-atc> bar once the user has scrolled past the
 * primary buy-buttons block. Only watches on viewports < 750px wide
 * (matches the CSS media query that displays the bar). Uses
 * IntersectionObserver on the primary buy buttons so the bar appears
 * only when the in-page CTA is no longer visible.
 */
class AofStickyAtc extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.footObserver = null;
    this.primaryCta = null;
    // True once the footer's bottom row is on screen. The bar stands down while
    // it is, so the last 64px of the footer stays readable.
    this.atFooter = false;
  }

  connectedCallback() {
    // Disable on tablet+ — desktop sticky info column handles ATC there
    if (window.matchMedia('(min-width: 750px)').matches) return;

    // Watch for resize → enable/disable observer dynamically
    this._mq = window.matchMedia('(min-width: 750px)');
    this._mqHandler = () => this._initOrTeardown();
    this._mq.addEventListener('change', this._mqHandler);

    // Defer until product-info has rendered (and buy buttons exist)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._initOrTeardown());
    } else {
      this._initOrTeardown();
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.footObserver) this.footObserver.disconnect();
    if (this._mq && this._mqHandler) {
      this._mq.removeEventListener('change', this._mqHandler);
    }
  }

  _initOrTeardown() {
    if (window.matchMedia('(min-width: 750px)').matches) {
      this.classList.remove('is-visible');
      this.setAttribute('aria-hidden', 'true');
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.footObserver) {
        this.footObserver.disconnect();
        this.footObserver = null;
      }
      return;
    }
    if (!this.observer) this._init();
  }

  /* Show or hide from the two signals together, so neither observer can
     contradict the other depending on which fired last. */
  _apply(ctaVisible) {
    if (typeof ctaVisible === 'boolean') this._ctaVisible = ctaVisible;
    var show = this._ctaVisible === false && !this.atFooter;
    this.classList.toggle('is-visible', show);
    this.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  /* The bar is pinned to the bottom of the viewport, so at the very end of the
     page it sits ON the footer's bottom row — the payment icons were half
     covered and the copyright and company-number lines could not be reached at
     all (measured at 390px: bar top 780, footer bottom 844). Standing down
     there costs nothing: by then the in-page buy button and the whole product
     page are long past, and the reason the bar exists — keeping "add to cart"
     reachable while reading — no longer applies. Reserving 64px of permanent
     dead space under the footer instead would have shown on every phone page
     whether the bar was on screen or not. */
  _watchFooter() {
    var foot = document.querySelector('.aof-footer__bottom');
    if (!foot) return;
    this.footObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.atFooter = entry.isIntersecting;
          this._apply();
        });
      },
      { threshold: 0 }
    );
    this.footObserver.observe(foot);
  }

  _init() {
    // Find the primary buy-buttons block to use as the visibility anchor.
    // Trade renders the primary ATC inside .product-form__buttons or
    // .product-form__submit; fall back to any element matching either.
    this.primaryCta =
      document.querySelector('.product-form__buttons') ||
      document.querySelector('.product-form__submit');
    if (!this.primaryCta) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Show the bar when the primary CTA is NOT visible — unless we have
          // reached the footer, which _apply() takes into account.
          this._apply(entry.isIntersecting);
        });
      },
      { rootMargin: '-80px 0px 0px 0px', threshold: 0 }
    );
    this.observer.observe(this.primaryCta);
    this._watchFooter();
  }
}

if (!customElements.get('aof-sticky-atc')) {
  customElements.define('aof-sticky-atc', AofStickyAtc);
}
